import Phaser from 'phaser';
import { grantFullGame } from '../../meta/unlock';
import { fullGameReveals } from '../../meta/reveals';
import type { RevealData } from './RevealScene';
import { haptics } from '../../platform/haptics';
import { openUrl } from '../../platform/native';
import {
  appStoreLink,
  buyFullGame,
  canPurchase,
  canRedeemCodes,
  fullGamePrice,
  ownsFullGame,
  redeemCode,
  storeName,
  type StorePrice,
} from '../../platform/purchases';
import { pixelPrice } from '../ui/price';
import { gameAudio } from '../audio/audio';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { label, UI } from '../ui/text';
import { wrapLineCount } from '../ui/wrap';
import { drawNeighborhood } from '../ui/zoneArt';
import { wipeTo } from './WipeScene';

export interface UnlockData {
  /** Where BACK goes, and what it is started with (the menu by default). */
  backTo?: string;
  backData?: object;
}

/** What the full game adds (docs/MONETIZATION.md). */
const PERKS = [
  '140 more lawns, 7 zones',
  'Bosses and secret yards',
  'Two more mowers',
  'The Lawn Builder',
  'A thank-you paint job',
];

type Busy = 'price' | 'buying' | 'restoring' | null;

/**
 * The full game's offer (docs/MONETIZATION.md, owner 2026-10-06): what it adds, then one honest
 * price from the store (Apple requires the store's own price string), Restore Purchases, and
 * Apple's sheet for a free code. On the web, where there is no store, a link to the app's store
 * page (Google Play on Android, the App Store elsewhere).
 * Reached from a locked lawn, the Backyard's NEXT ZONE card, and Settings; never mid-play.
 */
export class UnlockScene extends Phaser.Scene {
  private data_: UnlockData = {};
  private price: StorePrice | null = null;
  private busy: Busy = null;
  private note: { text: string; color: number } | null = null;
  /** Whether the full game was unlocked when last drawn (a redeemed code lands any time). */
  private drawnOwned = false;

  constructor() {
    super('Unlock');
  }

  init(data: UnlockData): void {
    this.data_ = data ?? {};
    this.price = null;
    this.note = null;
    this.busy = canPurchase() && !saves().save.fullGame ? 'price' : null;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    if (this.busy === 'price') void this.loadPrice();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    this.tweens.killAll();
    clearScene(this);
    this.draw();
  };

  private async loadPrice(): Promise<void> {
    this.busy = 'price';
    this.redraw();
    this.price = await fullGamePrice();
    this.busy = null;
    if (!this.price) this.note = { text: `${storeName()} can't be reached.`, color: UI.dim };
    this.redraw();
  }

  override update(): void {
    if (saves().save.fullGame !== this.drawnOwned) this.redraw();
  }

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const save = saves().save;
    this.drawnOwned = save.fullGame;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'FULL GAME', 2);

    // The next neighborhood over, waiting.
    const heroH = height >= 330 ? 54 : 40;
    drawNeighborhood(this, 26, heroH, 'park');
    g.lineStyle(1, UI.rule).strokeRect(-0.5, 25.5, width + 1, heroH + 1);

    // What it adds, in a navy panel.
    let y = 26 + heroH + 8;
    const textW = width - 28;
    const perksH = PERKS.reduce((h, t) => h + wrapLineCount(t, textW) * 10 + 2, 0);
    const owned = save.fullGame;
    const head = owned ? 'Thank you! Every lawn is open.' : 'One payment. No ads, ever.';
    const headH = wrapLineCount(head, textW) * 10;
    navyPanel(g, 4, y - 6, width - 8, headH + 4 + perksH + 10);
    label(this, 14, y, head, 1, UI.accent).setMaxWidth(textW);
    y += headH + 4;
    for (const t of PERKS) {
      label(this, 14, y, `- ${t}`, 1, owned ? UI.dim : UI.text).setMaxWidth(textW);
      y += wrapLineCount(t, textW) * 10 + 2;
    }
    y += 10;

    // A word on how the last try went.
    if (this.note) {
      label(this, width / 2, y, this.note.text, 1, this.note.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }

    // Buttons at the bottom, in thumb reach.
    const backY = height - 30;
    const back = this.data_.backTo ?? 'Menu';
    bevelButton(this, 6, backY, width - 12, 24, owned ? 'BACK' : 'NOT NOW', () =>
      wipeTo(this, back, this.data_.backData ?? {}),
    );
    if (owned) return;

    if (!canPurchase()) {
      // The web: the app is where the full game is.
      const { store, url } = appStoreLink();
      if (url) {
        bevelButton(
          this,
          6,
          backY - 34,
          width - 12,
          28,
          'GET THE APP',
          () => openUrl(url),
          true,
          2,
        );
      } else {
        label(this, width / 2, backY - 26, `Coming soon to ${store}`, 1, UI.accent).setOrigin(
          0.5,
          0,
        );
      }
      label(this, width / 2, backY - 50, 'Bring your lawns: Settings,', 1, UI.dim).setOrigin(
        0.5,
        0,
      );
      label(this, width / 2, backY - 40, 'then MOVE PROGRESS.', 1, UI.dim).setOrigin(0.5, 0);
      return;
    }

    // The native apps: the store's price, Restore Purchases, a free code.
    const buyY = backY - 34;
    const buyLabel =
      this.busy === 'buying'
        ? 'ONE MOMENT...'
        : this.busy === 'price'
          ? 'ASKING THE STORE...'
          : this.price
            ? `UNLOCK  ${pixelPrice(this.price)}`
            : 'TRY AGAIN';
    bevelButton(
      this,
      6,
      buyY,
      width - 12,
      28,
      buyLabel,
      () => {
        if (this.busy) return;
        if (this.price) void this.buy();
        else void this.loadPrice();
      },
      true,
      buyLabel.length * 12 + 8 <= width - 12 ? 2 : 1,
    );
    const half = Math.floor((width - 16) / 2);
    const smallY = buyY - 22;
    bevelButton(this, 6, smallY, canRedeemCodes() ? half : width - 12, 18, 'RESTORE', () => {
      if (!this.busy) void this.restore();
    });
    if (canRedeemCodes()) {
      bevelButton(this, 10 + half, smallY, half, 18, 'REDEEM CODE', () => {
        if (!this.busy) void redeemCode();
      });
    }
  }

  private async buy(): Promise<void> {
    this.busy = 'buying';
    this.note = null;
    this.redraw();
    const outcome = await buyFullGame();
    this.busy = null;
    if (outcome === 'bought') this.unlocked();
    else if (outcome === 'failed') {
      this.note = { text: 'The purchase did not go through.', color: UI.dim };
    }
    this.redraw();
  }

  private async restore(): Promise<void> {
    this.busy = 'restoring';
    this.note = { text: 'Checking with the store...', color: UI.dim };
    this.redraw();
    const owned = await ownsFullGame(true);
    this.busy = null;
    if (owned) this.unlocked();
    else {
      this.note = {
        text: owned === null ? "The store can't be reached." : 'No purchase found to restore.',
        color: UI.dim,
      };
    }
    this.redraw();
  }

  private unlocked(): void {
    // The mowers that come with the full game, where the player has got that far (the Lawn
    // Tractor after the Backyard): their NEW MOWER cards, over this screen.
    const reveals = fullGameReveals(saves().save);
    saves().update(grantFullGame);
    this.note = null;
    gameAudio.chime(true);
    haptics.impact();
    if (reveals.length > 0) {
      const data: RevealData = { reveals, done: () => this.redraw() };
      this.scene.launch('Reveal', data);
      this.scene.bringToTop('Reveal');
    }
  }
}
